import {
  DefaultButton,
  MessageBar,
  MessageBarType,
  Modal,
  PrimaryButton,
  Spinner,
  SpinnerSize,
  Stack,
  Text,
} from "@fluentui/react";
import { styles as sharedStyles } from "../../../Shared/shared.styles";
import { styles } from "../../../Shared/shared.confirmationModel.styles";
import { ExtensionDetailsType, PullRequestChange } from "../../../Types";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  createAdoBranch,
  createAdoPullRequest,
  pushToAdoBranch,
} from "../../../Api/ado.api";
import { EXTENSION_UPDATE_KEY } from "../../../Shared/shared.consts";
import { useEv2ExperimentParams } from "../../../Hooks/UseCurrentExtension";
import { useGetBranchDetails } from "../../../Hooks/UseGetBranchDetails";
import ConfirmationModelPrSection from "../../ExperimentDetails/ExperimentDetailsConfirmationModel/ConfirmationModelPrSection";
import useAppInsights from "../../../Hooks/UseAppInsights";
import { createBaseOperationTracker } from "../../../Shared/AppInsights";

interface ExtensionDetailsConfirmationModelProps {
  /**
   * The extension configuration details
   */
  extensionDetails: ExtensionDetailsType;
  /**
   * Optional url to pull request, if provided will perform an update the the existing pr
   */
  prUrl?: string;
  /**
   * True, this will create a new configuration, false is updating an existing configuration
   */
  isCreate?: boolean;
  /**
   * Callback function when user cancels out of the confirmation modal
   */
  onCancel: () => void;
  /**
   * Callback function called when pr is successfully created or updated. Closes the model and form, and shows a success toast.
   */
  onSaveSuccess: () => void;
}

/**
 * Confirmation modal that appears when user attempts to save changes to extension details.
 * Handles creating/updating pull request in Azure DevOps for the extension details configuration file in the repository.
 */
const ExtensionDetailsConfirmationModel: React.FC<
  ExtensionDetailsConfirmationModelProps
> = ({ extensionDetails, prUrl, isCreate, onCancel, onSaveSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { extensionName, extensionFolder } = useEv2ExperimentParams();
  const { trackOperationEvent } = useAppInsights(
    "ExtensionDetailsConfirmationModel",
  );
  const isMountedRef = useRef(true);
  const prAction = isCreate ? "Create" : "Update";

  // Track mounted state to prevent state updates after unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const { branchName, branchUrl } = useGetBranchDetails({
    branchNameModifier: `updateExtensionConfig-${extensionName}`,
  });

  const configJson = useMemo(() => {
    return JSON.stringify(extensionDetails, null, 2);
  }, [extensionDetails]);

  const handleOnSave = () => {
    trackOperationEvent({
      operation: "Save",
      function: "handleOnSave",
      properties: {
        extensionDetails,
      },
    });
    if (extensionFolder?.extensionRepoPath) {
      onSaveExtensionDetails(
        prAction,
        !!prUrl,
        branchName,
        extensionFolder.extensionRepoPath,
        extensionDetails,
        setIsLoading,
        onSaveSuccess,
        setError,
        isMountedRef,
      );
    }
  };

  return (
    <Modal isOpen={true} onDismiss={onCancel} isBlocking={true}>
      <Stack className={styles.confirmationModalContent}>
        <h2>{prAction} extension configurations</h2>
        {error && (
          <>
            <MessageBar messageBarType={MessageBarType.error}>
              {error}
            </MessageBar>
            <br />
          </>
        )}
        <Text>
          <em>
            Are you sure you want to {prAction.toLowerCase()} the extension
            configurations?
          </em>
        </Text>
        <MessageBar messageBarType={MessageBarType.warning}>
          Changes made to the extension configurations will impact all
          experiments under this extension
        </MessageBar>
        {prUrl ? (
          <Text>This will push and update the following PR:</Text>
        ) : (
          <Text>
            This will create a pull request to {prAction.toLocaleLowerCase()}{" "}
            the extension configurations, which will need to be approved and
            merged before changes take place. Link to the pull request will be
            provided upon creation.
          </Text>
        )}
        <ConfirmationModelPrSection
          configJson={configJson}
          configJsonFilename="base.json"
          prTitle={`[${EXTENSION_UPDATE_KEY}] ${prAction} extension configurations for ${extensionDetails.displayName}`}
          prUrl={prUrl}
          branchUrl={isCreate ? "" : branchUrl}
          branchName={branchName}
        />
        <div className={styles.confirmationModalActions}>
          <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 10 }}>
            <DefaultButton onClick={onCancel} disabled={isLoading}>
              No, cancel
            </DefaultButton>
            <PrimaryButton onClick={handleOnSave} disabled={isLoading}>
              {`${prAction} configurations`}
            </PrimaryButton>
          </Stack>
          {isLoading && (
            <div className={sharedStyles.spinnerOverlay}>
              <Spinner
                size={SpinnerSize.large}
                label={`${prAction.toLowerCase() === "create" ? "Creating" : "Updating"} configurations...`}
                labelPosition="bottom"
              />
            </div>
          )}
        </div>
      </Stack>
    </Modal>
  );
};

/**
 * Creates pull request for extension details configuration
 * @param prAction string to indicate whether this is a create or update action, used for commit and pr messages
 * @param isUpdate true, this is an update to existing PR, false if this is a new PR
 * @param branchName name of the branch to create or update the PR from
 * @param extensionPath path to the extension in the repository, accounts for nested extensions
 * @param extensionDetails full extension details object to be saved in the base.json file in the repository
 * @param setIsLoading function to set loading state in the confirmation modal
 * @param onSuccess callback function to be called on successful pull request creation, typically to close the confirmation modal and show a success message
 * @param setError callback function to set error state in the confirmation modal, takes a string error message as a parameter
 * @param isMountedRef ref to track if component is still mounted, prevents state updates after unmount
 */
const onSaveExtensionDetails = async (
  prAction: string,
  isUpdate: boolean,
  branchName: string,
  extensionPath: string,
  extensionDetails: ExtensionDetailsType,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  onSuccess: () => void,
  setError: React.Dispatch<React.SetStateAction<string | null>>,
  isMountedRef: React.MutableRefObject<boolean>,
) => {
  setIsLoading(true);
  const title = `[${EXTENSION_UPDATE_KEY}] ${prAction} extension configurations for ${extensionDetails.displayName}`;
  const description = `[${EXTENSION_UPDATE_KEY}] This PR ${prAction.toLowerCase()}s the extension configurations for ${extensionDetails.displayName}.`;
  const tracker = createBaseOperationTracker(
    "SaveExtensionDetails",
    "onSaveExtensionDetails",
    {
      component: "ExtensionDetailsConfirmationModel",
      extensionName: extensionPath,
    },
  );
  tracker.start({ branchName, isUpdate, prAction });
  try {
    await createAdoBranch(branchName, "main");
    tracker.step("BranchCreated", { branchName });

    const itemPath = `Extensions/${extensionPath}/base.json`;
    const fileContent = JSON.stringify(extensionDetails, null, 2);
    const changes: PullRequestChange[] = [
      {
        changeType: isUpdate ? "edit" : "add",
        itemPath: itemPath,
        newContent: fileContent,
        contentType: "rawtext",
      },
    ];
    const commitMessage = `${prAction.toLowerCase() === "create" ? "Creating" : "Updating"} extension configurations for ${extensionDetails.displayName}`;
    await pushToAdoBranch({ branchName, commitMessage, changes });
    tracker.step("FilesPushed", { branchName, commitMessage });

    if (!isUpdate) {
      await createAdoPullRequest({
        sourceBranch: branchName,
        targetBranch: "main",
        title,
        description,
      });
      tracker.step("PullRequestCreated", { branchName });
    }
    tracker.succeed();
  } catch (error) {
    const errorMessage = `Failed to update extension configurations: ${(error as Error).message}`;
    tracker.fail(errorMessage);
    if (isMountedRef.current) {
      setError(errorMessage);
    }
    return;
  } finally {
    if (isMountedRef.current) {
      setIsLoading(false);
    }
  }
  if (isMountedRef.current) {
    onSuccess();
  }
};

export default ExtensionDetailsConfirmationModel;
